import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterSidebar = ({ filters, onFiltersChange, savedPresets, onSavePreset, onLoadPreset }) => {
  const [presetName, setPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'closing', label: 'Closing Soon' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const stateOptions = [
    { value: '', label: 'All States' },
    { value: 'CA', label: 'California' },
    { value: 'TX', label: 'Texas' },
    { value: 'FL', label: 'Florida' },
    { value: 'NY', label: 'New York' },
    { value: 'IL', label: 'Illinois' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'OH', label: 'Ohio' },
    { value: 'GA', label: 'Georgia' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'MI', label: 'Michigan' }
  ];

  const buyerCountOptions = [
    { value: '', label: 'Any Count' },
    { value: 1, label: '1+ Buyers' },
    { value: 2, label: '2+ Buyers' },
    { value: 3, label: '3+ Buyers' },
    { value: 5, label: '5+ Buyers' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleBidRangeChange = (type, value) => {
    onFiltersChange({
      ...filters,
      bidRange: {
        ...filters?.bidRange,
        [type]: value ? parseFloat(value) : null
      }
    });
  };

  const handleSavePreset = () => {
    if (presetName?.trim()) {
      onSavePreset(presetName?.trim(), filters);
      setPresetName('');
      setShowSavePreset(false);
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      status: 'all',
      state: '',
      bidRange: { min: null, max: null },
      buyerCount: '',
      search: ''
    });
  };

  const hasActiveFilters = () => {
    return filters?.status !== 'all' || 
           filters?.state !== '' || 
           filters?.bidRange?.min !== null || 
           filters?.bidRange?.max !== null || 
           filters?.buyerCount !== '' || 
           filters?.search !== '';
  };

  return (
    <div className="w-80 bg-card border-r border-border h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Filters</h3>
          {hasActiveFilters() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-xs"
            >
              Clear All
            </Button>
          )}
        </div>
        
        {/* Search */}
        <Input
          type="search"
          placeholder="Search auctions..."
          value={filters?.search}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="mb-3"
        />
      </div>
      {/* Filter Sections */}
      <div className="p-4 space-y-6">
        {/* Auction Status */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Auction Status
          </label>
          <Select
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            placeholder="Select status"
          />
        </div>

        {/* Geographic Filter */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            State
          </label>
          <Select
            options={stateOptions}
            value={filters?.state}
            onChange={(value) => handleFilterChange('state', value)}
            placeholder="Select state"
            searchable
          />
        </div>

        {/* Bid Range */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Bid Range
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="Min $"
              value={filters?.bidRange?.min || ''}
              onChange={(e) => handleBidRangeChange('min', e?.target?.value)}
            />
            <Input
              type="number"
              placeholder="Max $"
              value={filters?.bidRange?.max || ''}
              onChange={(e) => handleBidRangeChange('max', e?.target?.value)}
            />
          </div>
        </div>

        {/* Buyer Participation */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Buyer Participation
          </label>
          <Select
            options={buyerCountOptions}
            value={filters?.buyerCount}
            onChange={(value) => handleFilterChange('buyerCount', value)}
            placeholder="Select minimum buyers"
          />
        </div>

        {/* Advanced Filters */}
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-medium text-foreground mb-3">Advanced</h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">High Priority Only</span>
              <input
                type="checkbox"
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                checked={filters?.highPriorityOnly || false}
                onChange={(e) => handleFilterChange('highPriorityOnly', e?.target?.checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Closing Soon (&lt;2min)</span>
              <input
                type="checkbox"
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                checked={filters?.closingSoon || false}
                onChange={(e) => handleFilterChange('closingSoon', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Saved Presets */}
      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-foreground">Saved Presets</h4>
          <Button
            variant="ghost"
            size="sm"
            iconName="Plus"
            onClick={() => setShowSavePreset(!showSavePreset)}
            title="Save current filters"
          />
        </div>

        {showSavePreset && (
          <div className="mb-3 p-3 bg-muted/30 rounded-lg">
            <Input
              type="text"
              placeholder="Preset name"
              value={presetName}
              onChange={(e) => setPresetName(e?.target?.value)}
              className="mb-2"
            />
            <div className="flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleSavePreset}
                disabled={!presetName?.trim()}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowSavePreset(false);
                  setPresetName('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {savedPresets?.map((preset) => (
            <div
              key={preset?.id}
              className="flex items-center justify-between p-2 bg-muted/20 rounded hover:bg-muted/40 transition-smooth"
            >
              <button
                onClick={() => onLoadPreset(preset)}
                className="flex-1 text-left text-sm text-foreground hover:text-accent transition-smooth"
              >
                {preset?.name}
              </button>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => {
                  // Handle delete preset
                }}
                title="Delete preset"
              />
            </div>
          ))}
          
          {savedPresets?.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              No saved presets
            </p>
          )}
        </div>
      </div>
      {/* Quick Stats */}
      <div className="border-t border-border p-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Quick Stats</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Active Auctions</span>
            <span className="text-foreground font-medium">247</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Avg Bid</span>
            <span className="text-foreground font-medium">$32.50</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Buyers</span>
            <span className="text-foreground font-medium">89</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;